import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { queryKeys } from "@/lib/query-keys";
import { activityLogService } from "@/services/activity-log";

import type { ActivityLog } from "@artisancode/api-types";

export function useActivityLogs() {
  const [q, setQ] = useState("");
  const [entityName, setEntityName] = useState("all");
  const [activity, setActivity] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [workspaceId, setWorkspaceId] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 20;

  const [selectedLog, setSelectedLog] = useState<ActivityLog | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const queryParams = {
    q: q.trim() || undefined,
    entityName: entityName === "all" ? undefined : entityName,
    activity: activity === "all" ? undefined : activity,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    workspace_id: workspaceId || undefined,
    pagination: {
      page,
      per_page: perPage,
    },
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: queryKeys.activityLogs.list(queryParams),
    queryFn: () => activityLogService.list(queryParams),
    placeholderData: (prev) => prev,
  });

  const resetFilters = () => {
    setQ("");
    setEntityName("all");
    setActivity("all");
    setDateFrom("");
    setDateTo("");
    setWorkspaceId("");
    setPage(1);
  };

  const openDetail = (log: ActivityLog) => {
    setSelectedLog(log);
    setDetailOpen(true);
  };

  const closeDetail = () => {
    setDetailOpen(false);
    setSelectedLog(null);
  };

  return {
    // Filter states
    q,
    setQ: (val: string) => {
      setQ(val);
      setPage(1);
    },
    entityName,
    setEntityName: (val: string) => {
      setEntityName(val);
      setPage(1);
    },
    activity,
    setActivity: (val: string) => {
      setActivity(val);
      setPage(1);
    },
    dateFrom,
    setDateFrom: (val: string) => {
      setDateFrom(val);
      setPage(1);
    },
    dateTo,
    setDateTo: (val: string) => {
      setDateTo(val);
      setPage(1);
    },
    setDateRange: (from: string, to: string) => {
      setDateFrom(from);
      setDateTo(to);
      setPage(1);
    },
    workspaceId,
    setWorkspaceId: (val: string) => {
      setWorkspaceId(val);
      setPage(1);
    },
    page,
    setPage,
    resetFilters,

    // Query states
    items: data?.items ?? [],
    pagination: data?.pagination,
    isLoading,
    isError,
    refetch,

    // Dialog state
    selectedLog,
    detailOpen,
    openDetail,
    closeDetail,
  };
}

export function useEntityActivityLogs({
  entityName,
  entityId,
  enabled = true,
}: {
  entityName: string;
  entityId?: string | null;
  enabled?: boolean;
}) {
  const pagination = { per_page: 100 };

  return useQuery({
    queryKey: queryKeys.activityLogs.entity(entityName, entityId, pagination),
    queryFn: () => {
      if (!entityId) {
        throw new Error("entityId is required");
      }
      return activityLogService.getByEntity(entityName, entityId, {
        pagination,
      });
    },
    enabled: enabled && Boolean(entityId),
  });
}
